import { getHistory } from "../api/endpoints";
import type { HistoryResponse, RollupBar, Tick, Timeframe } from "../api/types";
import { DEFAULT_BAND_WIDTH_USD, TIMEFRAME_MINUTES } from "../api/types";
import { bucketize, computeWindow, type Bucket, type RatioPoint, type RatioSpan } from "./window";

export interface ChartData {
  buckets: Bucket[];
  resolution: string;
  truncated: boolean;
}

// Incremental-refresh cache: one full pull per (timeframe, periods), then
// every 60s poll sends `after_ts` = the newest row we hold (+ the pinned
// resolution, so server auto-selection can't switch tables mid-series) and
// gets back only the strictly-newer rows -- ~KBs instead of re-downloading
// the whole multi-MB window. Rows are immutable once served (raw ticks by
// construction; rollup bars because btclock-ingest only writes fully
// elapsed periods -- rollup.py "never touches the period containing the
// most recent tick"), so merging is append + evict-out-of-window. A tab
// that slept past the window degrades gracefully: the delta simply returns
// the full window again and eviction drops everything stale.
interface HistoryCache {
  key: string;
  resolution: string;
  ticks: Tick[];
  bars: RollupBar[];
  cursor: string; // newest ts_utc (raw) / period_start_utc (rollups) held
}

// One cache per band (keyed by band width, "" = default) so the main chart
// and an admin research-band chart never clobber each other's cursor.
const cacheByBand = new Map<string, HistoryCache>();

function newestCursor(res: HistoryResponse): string | null {
  if (res.ticks.length > 0) return res.ticks[res.ticks.length - 1].ts_utc;
  if (res.bars.length > 0) return res.bars[res.bars.length - 1].period_start_utc;
  return null;
}

export async function fetchChartData(
  timeframe: Timeframe,
  numPeriods: number,
  signal?: AbortSignal,
  // Research band (admin-only server-side). Omitted = the default band,
  // exactly today's request. Research bands are served raw-only (rollups
  // are default-band-only), so the full pull pins resolution=raw.
  bandWidthUsd?: number
): Promise<ChartData> {
  const periodMs = TIMEFRAME_MINUTES[timeframe] * 60_000;
  const nowMs = Date.now();
  const window = computeWindow(periodMs, numPeriods, nowMs);
  const minutes = Math.ceil((nowMs - window.sinceMs) / 60_000);
  const key = `${timeframe}:${numPeriods}`;
  const bandKey = bandWidthUsd === undefined ? "" : String(bandWidthUsd);
  const research = bandWidthUsd !== undefined && bandWidthUsd !== DEFAULT_BAND_WIDTH_USD;
  const cache = cacheByBand.get(bandKey);

  let resolution: string;
  let truncated: boolean;
  let ticks: Tick[];
  let bars: RollupBar[];

  if (cache && cache.key === key) {
    const delta = await getHistory(minutes, signal, {
      resolution: cache.resolution,
      afterTs: cache.cursor,
      bandWidthUsd,
    });
    // Server timestamps are one consistent ISO format, but the window edge
    // is client-computed ms -- evict by parsed time, never string compares
    // across formats.
    ticks = cache.ticks
      .concat(delta.ticks)
      .filter((t) => new Date(t.ts_utc).getTime() >= window.sinceMs);
    bars = cache.bars
      .concat(delta.bars)
      .filter((b) => new Date(b.period_start_utc).getTime() >= window.sinceMs);
    resolution = cache.resolution;
    truncated = delta.truncated;
    cacheByBand.set(bandKey, { key, resolution, ticks, bars, cursor: newestCursor(delta) ?? cache.cursor });
  } else {
    const full = await getHistory(minutes, signal, {
      bandWidthUsd,
      resolution: research ? "raw" : undefined,
    });
    ticks = full.ticks;
    bars = full.bars;
    resolution = full.resolution;
    truncated = full.truncated;
    const cursor = newestCursor(full);
    // No rows yet (empty window) -> no cursor to poll from; keep doing
    // full pulls until one returns data.
    if (cursor === null) cacheByBand.delete(bandKey);
    else cacheByBand.set(bandKey, { key, resolution, ticks, bars, cursor });
  }

  const points: RatioPoint[] = ticks
    .filter((t: Tick) => t.ratio !== null)
    .map((t: Tick) => ({
      tsMs: new Date(t.ts_utc).getTime(),
      ratio: t.ratio as number,
      // T-156: true per-second extremes; null/absent rows fall back to the
      // sampled ratio inside bucketize.
      windowMax: t.ratio_window_max,
      windowMin: t.ratio_window_min,
    }));

  const spans: RatioSpan[] = bars
    .filter(
      (b: RollupBar) => b.high_ratio !== null && b.low_ratio !== null && b.avg_ratio !== null
    )
    .map((b: RollupBar) => {
      const startMs = new Date(b.period_start_utc).getTime();
      return {
        startMs,
        endMs: startMs, // only used as a fallback; assignment is by start time (see window.ts)
        high: b.high_ratio as number,
        low: b.low_ratio as number,
        avg: b.avg_ratio as number,
        weight: Math.max(b.tick_count, 1),
        // Real split computed by btclock-ingest at rollup time (2026-07-22)
        // -- null for rows that predate this column, never re-derived from
        // the already-blended avg_ratio.
        bullishAvg: b.bullish_avg_ratio,
        bearishAvg: b.bearish_avg_ratio,
      };
    });

  const buckets = bucketize(points, spans, periodMs, numPeriods, nowMs);

  return { buckets, resolution, truncated };
}

/** Test hook: drop the incremental cache so a test (or a hard reset) starts
 * from a clean full-pull state. */
export function resetHistoryCache(): void {
  cacheByBand.clear();
}
